import React, { useCallback, useState, useSyncExternalStore } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  clearCachedDeferredInstallPrompt,
  getDeferredInstallPromptSnapshot,
  subscribeDeferredInstallPrompt,
} from '../utils/pwaInstallPrompt';
import { isPwaStandalone } from '../utils/pwaStandalone';

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export interface PwaInstallButtonProps {
  className?: string;
  /** Hide label; icon + aria-label only (e.g. mobile navbar). */
  iconOnly?: boolean;
}

/**
 * "Install" / download control: uses `beforeinstallprompt` on Chromium;
 * on iOS Safari offers instructions (no programmatic install).
 */
const PwaInstallButton: React.FC<PwaInstallButtonProps> = ({ className, iconOnly }) => {
  const { t } = useSafeTranslation();
  const deferredPrompt = useSyncExternalStore(
    subscribeDeferredInstallPrompt,
    getDeferredInstallPromptSnapshot,
    () => null
  );
  const [showIosModal, setShowIosModal] = useState(false);

  const handleChromiumInstall = useCallback(async () => {
    const p = getDeferredInstallPromptSnapshot();
    if (!p) return;
    await p.prompt();
    await p.userChoice;
    clearCachedDeferredInstallPrompt();
  }, []);

  if (isPwaStandalone()) {
    return null;
  }

  const compactBtnClass = iconOnly
    ? [className, 'd-inline-flex align-items-center justify-content-center pwa-install-btn--icon-only']
        .filter(Boolean)
        .join(' ')
    : className;

  if (deferredPrompt) {
    return (
      <Button
        type="button"
        variant="outline-primary"
        size="sm"
        className={compactBtnClass}
        onClick={handleChromiumInstall}
        title={iconOnly ? t('common.installApp', 'Install app') : undefined}
        aria-label={t('common.installAppAria', 'Download or install the app')}
      >
        <i className={`bi bi-download${iconOnly ? '' : ' me-1'}`} aria-hidden />
        {!iconOnly && t('common.installApp', 'Install app')}
      </Button>
    );
  }

  if (isIosDevice()) {
    return (
      <>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          className={compactBtnClass}
          onClick={() => setShowIosModal(true)}
          title={iconOnly ? t('common.installApp', 'Install app') : undefined}
          aria-label={t('common.installAppAria', 'Download or install the app')}
        >
          <i className={`bi bi-download${iconOnly ? '' : ' me-1'}`} aria-hidden />
          {!iconOnly && t('common.installApp', 'Install app')}
        </Button>
        <Modal show={showIosModal} onHide={() => setShowIosModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('common.iosInstallTitle', 'Add to Home Screen')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-body-secondary" style={{ whiteSpace: 'pre-line' }}>
            {t(
              'common.iosInstallBody',
              '1. Tap Share in the Safari toolbar.\n2. Scroll down and tap "Add to Home Screen".\n3. Confirm to install Quizter Club on your device.'
            )}
          </Modal.Body>
        </Modal>
      </>
    );
  }

  return null;
};

export default PwaInstallButton;
